import { useState, useEffect, useCallback } from 'react';
import { getCalendarEvents, formatMonthForAPI, isValidMonthFormat } from '../api';
import { useDemoMode } from '../providers/DemoModeProvider';

// Demo calendar events for demo mode
const generateMockCalendarEvents = (month) => {
  const startDate = new Date(month + '-01');
  const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
  const events = [];
  
  for (let day = 1; day <= endDate.getDate(); day++) {
    if (Math.random() > 0.7) { // 30% chance of events on any given day
      const eventDate = new Date(startDate.getFullYear(), startDate.getMonth(), day);
      const numEvents = Math.floor(Math.random() * 3) + 1; // 1-3 events per day
      
      for (let i = 0; i < numEvents; i++) {
        const hour = 9 + Math.floor(Math.random() * 12); // Between 9 AM and 9 PM
        const minute = Math.floor(Math.random() * 4) * 15; // 0, 15, 30, or 45
        
        const eventDateTime = new Date(eventDate);
        eventDateTime.setHours(hour, minute, 0, 0);
        
        const platforms = ['twitter', 'instagram', 'linkedin', 'facebook', 'tiktok'];
        const platform = platforms[Math.floor(Math.random() * platforms.length)];
        
        const contents = [
          "🚀 Exciting product update announcement",
          "💡 Industry insights and tips",
          "📊 Weekly performance highlights",
          "🎯 Marketing strategy deep dive",
          "✨ Behind the scenes content",
          "📝 Educational content for our audience",
          "🔥 Trending topic discussion",
          "💼 Professional networking post"
        ];
        
        events.push({
          post_id: `demo-${day}-${i}-${platform}`,
          title: contents[Math.floor(Math.random() * contents.length)],
          start: eventDateTime.toISOString(),
          end: new Date(eventDateTime.getTime() + 60000).toISOString(), // 1 minute duration
          platform: platform,
          status: Math.random() > 0.2 ? 'scheduled' : 'published',
          content: contents[Math.floor(Math.random() * contents.length)]
        });
      }
    }
  }
  
  return events.sort((a, b) => new Date(a.start) - new Date(b.start));
};

/**
 * Custom hook for managing calendar events
 * @param {string} initialMonth - Initial month in YYYY-MM format
 * @returns {Object} Hook state and functions
 */
export const useCalendarEvents = (initialMonth = null) => {
  const { isDemoMode } = useDemoMode();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(
    initialMonth || formatMonthForAPI()
  );
  const [eventsCache, setEventsCache] = useState(new Map());

  const fetchEvents = useCallback(async (month = currentMonth) => {
    if (!month || !isValidMonthFormat(month)) {
      setError('Invalid month format');
      return;
    }
    
    // Check cache first
    if (eventsCache.has(month)) {
      setEvents(eventsCache.get(month));
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      if (isDemoMode) {
        // Use demo data when demo mode is enabled
        const demoEvents = generateMockCalendarEvents(month);
        setEvents(demoEvents);
        setEventsCache(prev => new Map(prev).set(month, demoEvents));
      } else {
        const response = await getCalendarEvents(month);
        const calendarEvents = response.calendar_events || [];
        
        setEvents(calendarEvents);
        
        // Cache the events
        setEventsCache(prev => new Map(prev).set(month, calendarEvents));
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setError(err.message || 'Failed to fetch calendar events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [currentMonth, eventsCache, isDemoMode]);

  // Fetch events when month changes
  useEffect(() => {
    fetchEvents(currentMonth);
  }, [currentMonth, fetchEvents]);

  const changeMonth = useCallback((newMonth) => {
    if (newMonth !== currentMonth && isValidMonthFormat(newMonth)) {
      setCurrentMonth(newMonth);
    }
  }, [currentMonth]);

  const navigateMonth = useCallback((direction) => {
    const date = new Date(currentMonth + '-01');
    if (direction === 'prev') {
      date.setMonth(date.getMonth() - 1);
    } else if (direction === 'next') {
      date.setMonth(date.getMonth() + 1);
    }
    const newMonth = formatMonthForAPI(date);
    setCurrentMonth(newMonth);
  }, [currentMonth]);

  const goToToday = useCallback(() => {
    const today = formatMonthForAPI();
    setCurrentMonth(today);
  }, []);

  const refreshEvents = useCallback(() => {
    // Clear cache for current month to force refresh
    setEventsCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(currentMonth);
      return newCache;
    });
    fetchEvents(currentMonth);
  }, [fetchEvents, currentMonth]);

  const clearCache = useCallback(() => {
    setEventsCache(new Map());
  }, []);

  // Preload adjacent months for better UX
  const preloadAdjacentMonths = useCallback(async () => {
    const currentDate = new Date(currentMonth + '-01');
    
    const prevMonth = new Date(currentDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const prevMonthStr = formatMonthForAPI(prevMonth);
    
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const nextMonthStr = formatMonthForAPI(nextMonth);
    
    // Preload if not in cache
    [prevMonthStr, nextMonthStr].forEach(month => {
      if (!eventsCache.has(month)) {
        getCalendarEvents(month)
          .then(response => {
            setEventsCache(prev => new Map(prev).set(month, response.calendar_events || []));
          })
          .catch(err => {
            console.warn(`Failed to preload events for ${month}:`, err);
          });
      }
    });
  }, [currentMonth, eventsCache]);

  // Preload adjacent months when current month events are loaded
  useEffect(() => {
    if (events.length > 0 || (!loading && !error)) {
      const timer = setTimeout(preloadAdjacentMonths, 1000);
      return () => clearTimeout(timer);
    }
  }, [events, loading, error, preloadAdjacentMonths]);

  const getEventById = useCallback((eventId) => {
    return events.find(event => event.post_id === eventId);
  }, [events]);

  const getEventsByDate = useCallback((date) => {
    const targetDate = new Date(date).toISOString().split('T')[0];
    return events.filter(event => {
      const eventDate = new Date(event.start).toISOString().split('T')[0];
      return eventDate === targetDate;
    });
  }, [events]);

  return {
    events,
    loading,
    error,
    currentMonth,
    changeMonth,
    navigateMonth,
    goToToday,
    refreshEvents,
    clearCache,
    getEventById,
    getEventsByDate,
    retry: refreshEvents,
    eventsCount: events.length
  };
}; 